import { useEffect, useState, useCallback } from "react";
import { useSteps } from "./hooks/useSteps";
import { useHubSpotProducts } from "./hooks/useHubSpotProducts";
import { useHubSpotFields } from "./hooks/useHubSpotFields";
import { saveFlow, loadFlow, fetchItemImages, uploadItemImage, deleteItemImage } from "./api/flows";
import type { ItemImage } from "./types/configurator";
import { CatalogSidebar } from "./components/CatalogSidebar/CatalogSidebar";
import { StepBuilder } from "./components/StepBuilder/StepBuilder";
import { PreviewPanel } from "./components/Preview/PreviewPanel";
import { CacheControl } from "./components/CacheControl";
import { FlowSelector } from "./components/FlowSelector/FlowSelector";
import { FieldManagement } from "./components/FieldManagement/FieldManagement";
import { ProductManagement } from "./components/ProductManagement/ProductManagement";
import { DebugQuotes } from "./components/QuoteDebug/DebugQuotes";
import "./styles/theme.css";
import "./App.css";

type View = { type: 'flows' } | { type: 'fields' } | { type: 'products' } | { type: 'debug-quotes' } | { type: 'editor'; id: string };

function getViewFromHash(): View {
  const hash = window.location.hash;
  const flowMatch = hash.match(/^#\/flows\/(.+)$/);
  if (flowMatch) return { type: 'editor', id: decodeURIComponent(flowMatch[1]) };
  if (hash === '#/fields') return { type: 'fields' };
  if (hash === '#/products') return { type: 'products' };
  if (hash === '#/debug-quotes') return { type: 'debug-quotes' };
  return { type: 'flows' };
}

export default function App() {
  const [view, setView] = useState<View>(getViewFromHash);

  useEffect(() => {
    const onHashChange = () => setView(getViewFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleSelectFlow = (id: string) => {
    window.location.hash = `/flows/${encodeURIComponent(id)}`;
  };

  const handleBack = () => {
    window.location.hash = '';
  };

  const handleFieldManagement = () => {
    window.location.hash = '/fields';
  };

  const handleProductManagement = () => {
    window.location.hash = '/products';
  };

  if (view.type === 'fields') {
    return <FieldManagement />;
  }

  if (view.type === 'products') {
    return <ProductManagement />;
  }

  if (view.type === 'debug-quotes') {
    return <DebugQuotes />;
  }

  if (view.type === 'editor') {
    return <Editor flowId={view.id} onBack={handleBack} />;
  }

  return <FlowSelector
      onSelect={handleSelectFlow}
      onFieldManagement={handleFieldManagement}
      onProductManagement={handleProductManagement}
    />;
}

function Editor({ flowId, onBack }: { flowId: string; onBack: () => void }) {
  const [showCache, setShowCache] = useState(false);
  const {
    categories,
    productMap,
    loading,
    error,
    refetch,
    search,
  } = useHubSpotProducts();

  const { fields, loading: fieldsLoading, refetch: refetchFields } = useHubSpotFields();

  const {
    steps,
    resetSteps,
    addStep,
    duplicateStep,
    removeStep,
    updateStep,
    reorderStep,
    addProductToStep,
    addFieldValueToStep,
    removeProductFromStep,
    setConditions,
    setExclusions,
    getStepIndex,
  } = useSteps([]);

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [itemImageMap, setItemImageMap] = useState<Record<string, ItemImage[]>>({});
  const [flowName, setFlowName] = useState('');

  // Load saved steps and images when flow changes
  useEffect(() => {
    loadFlow(flowId).then((saved) => {
      if (saved) {
        resetSteps(saved.steps);
        setFlowName(saved.name);
      }
    }).catch(console.error);

    fetchItemImages().then(setItemImageMap).catch(console.error);
  }, [flowId]);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await saveFlow(flowId, steps, flowName);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleItemImageUpload = useCallback(async (itemKey: string, file: File) => {
    const img = await uploadItemImage(itemKey, file);
    setItemImageMap((prev) => ({
      ...prev,
      [itemKey]: [...(prev[itemKey] || []), img],
    }));
  }, []);

  const handleItemImageDelete = useCallback(async (imageId: string, itemKey: string) => {
    await deleteItemImage(imageId);
    setItemImageMap((prev) => ({
      ...prev,
      [itemKey]: (prev[itemKey] || []).filter((img) => img.id !== imageId),
    }));
  }, []);

  return (
    <div className="app">
      {/* Error banner */}
      {error && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ff5722',
          color: 'white',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <span>
            <strong>Error loading products:</strong> {error}
          </span>
          <button
            onClick={refetch}
            style={{
              backgroundColor: 'white',
              color: '#ff5722',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Retry
          </button>
        </div>
      )}

      <CatalogSidebar
        categories={categories}
        onSearch={search}
        loading={loading}
        fields={fields}
        fieldsLoading={fieldsLoading}
      />

      <StepBuilder
        steps={steps}
        productMap={productMap}
        itemImageMap={itemImageMap}
        onAddStep={addStep}
        onDuplicateStep={duplicateStep}
        onRemoveStep={removeStep}
        onUpdateStep={updateStep}
        onReorderStep={reorderStep}
        onAddProduct={addProductToStep}
        onAddFieldValue={addFieldValueToStep}
        onRemoveProduct={removeProductFromStep}
        onSetConditions={setConditions}
        onSetExclusions={setExclusions}
        onItemImageUpload={handleItemImageUpload}
        onItemImageDelete={handleItemImageDelete}
        getStepIndex={getStepIndex}

        onSave={handleSave}
        saving={saving}
        saveStatus={saveStatus}
        onBack={onBack}
        flowName={flowName}
        onFlowNameChange={setFlowName}
      />

      <PreviewPanel steps={steps} productMap={productMap} />

      <footer className="app-footer">
        {showCache && (
          <CacheControl onRefresh={async () => { await Promise.all([refetch(), refetchFields()]); }} />
        )}
        <button
          className="btn btn-sm"
          onClick={() => setShowCache((v) => !v)}
          title="Toggle cache info"
          style={{ padding: '4px 8px', fontSize: '18px', lineHeight: 1 }}
        >
          ⚙
        </button>
      </footer>
    </div>
  );
}
